import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  PenLine, Building2, Loader2, Save, Plus, Trash2, Eye, Palette, Info,
  Copy, Check, Code2, ChevronDown, Mail,
} from "lucide-react";
import { api, apiErrorMessage } from "../../lib/api";
import { copyRichText, copyPlainText } from "../../lib/clipboard";

/**
 * Email signature editor. Left: the form. Right: a live preview that is the
 * SAME html the export will hand over, fetched from the backend renderer —
 * never rebuilt client-side, or the two would drift.
 *
 * Split by permission, mirroring the backend:
 *   • "Datele mele"  — every user of this tab edits their own.
 *   • "Brand comun"  — owner writes, admin_fped sees it read-only. Editing it
 *     restyles the whole team's signature.
 */

const MAX_SOCIALS = 6;

// Mirrors the length caps on the backend models. Enforcing them here too turns
// a 422 into a field that simply stops accepting input.
const LIMITS = {
  full_name: 120, role_title: 120, phone: 40, email: 160,
  org_name: 160, site_label: 80, address: 240, disclaimer: 600,
  logo_url: 500, site_url: 500, social_label: 40, social_url: 500,
};

const TOGGLES = [
  ["show_phone", "Arată telefonul"],
  ["show_email", "Arată emailul"],
  ["show_socials", "Arată linkurile sociale"],
  ["show_disclaimer", "Arată nota de confidențialitate"],
];

const COLORS = [
  ["accent_color", "Accent"],
  ["link_color", "Linkuri"],
  ["text_color", "Text"],
  ["muted_color", "Text secundar"],
];

export default function SignatureTab({ isOwner }) {
  const [profile, setProfile] = useState(null);
  const [brand, setBrand] = useState(null);
  const [savedProfile, setSavedProfile] = useState(null);
  const [savedBrand, setSavedBrand] = useState(null);
  const [html, setHtml] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/signature/me");
      setProfile(r.data.profile);
      setSavedProfile(r.data.profile);
      setBrand(r.data.brand);
      setSavedBrand(r.data.brand);
      setHtml(r.data.html);
      setText(r.data.text);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Nu am putut încărca semnătura."));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const profileDirty = useMemo(
    () => JSON.stringify(profile) !== JSON.stringify(savedProfile),
    [profile, savedProfile],
  );
  const brandDirty = useMemo(
    () => JSON.stringify(brand) !== JSON.stringify(savedBrand),
    [brand, savedBrand],
  );

  // ---- live preview -------------------------------------------------------
  // Debounced so typing a name is not one request per keystroke. `skipFirst`
  // keeps the initial render on the html the GET already returned.
  const skipFirst = useRef(true);
  useEffect(() => {
    if (!profile || !brand) return;
    if (skipFirst.current) { skipFirst.current = false; return; }
    const t = setTimeout(async () => {
      setPreviewing(true);
      try {
        const r = await api.post("/admin/signature/preview", { profile, brand });
        setHtml(r.data.html);
        setText(r.data.text);
      } catch {
        // A 422 here just means the brand form is mid-edit (e.g. a half-typed
        // URL). The last good preview stays on screen; the save button is what
        // surfaces the real error.
      } finally {
        setPreviewing(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [profile, brand]);

  const saveProfile = async () => {
    setBusy("profile");
    try {
      const r = await api.put("/admin/signature/me", profile);
      setProfile(r.data.profile);
      setSavedProfile(r.data.profile);
      setHtml(r.data.html);
      setText(r.data.text);
      toast.success("Semnătura ta a fost salvată.");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Eroare la salvarea semnăturii."));
    } finally {
      setBusy(null);
    }
  };

  const saveBrand = async () => {
    setBusy("brand");
    try {
      const r = await api.put("/admin/signature/brand", brand);
      setBrand(r.data);
      setSavedBrand(r.data);
      toast.success("Brandul comun a fost salvat.");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Eroare la salvarea brandului."));
    } finally {
      setBusy(null);
    }
  };

  const setP = (k, v) => setProfile((p) => ({ ...p, [k]: v }));
  const setB = (k, v) => setBrand((b) => ({ ...b, [k]: v }));

  if (!profile || !brand) {
    return (
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-8 flex items-center gap-2 text-[#6B7280]">
        <Loader2 className="animate-spin" size={18} /> Se încarcă semnătura…
      </div>
    );
  }

  const socials = brand.socials || [];

  return (
    <div data-testid="signature-panel" className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
      {/* ------------------------------- FORM ------------------------------ */}
      <div className="space-y-4">
        <Card title="Datele mele" icon={<PenLine size={16} />} testid="signature-card-profile">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Nume complet" value={profile.full_name} max={LIMITS.full_name}
                   onChange={(v) => setP("full_name", v)} placeholder="ex: Ana Popescu"
                   testid="signature-full-name" />
            <Field label="Funcție" value={profile.role_title} max={LIMITS.role_title}
                   onChange={(v) => setP("role_title", v)} placeholder="ex: Director de programe"
                   testid="signature-role-title" />
            <Field label="Telefon" value={profile.phone} max={LIMITS.phone}
                   onChange={(v) => setP("phone", v)} placeholder="ex: +40 722 123 456"
                   testid="signature-phone" />
            <Field label="Email afișat" value={profile.email} max={LIMITS.email}
                   onChange={(v) => setP("email", v)} placeholder="ex: nume@fped.org"
                   hint="Poate diferi de emailul cu care te autentifici."
                   testid="signature-email" />
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {TOGGLES.map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 text-sm text-[#373A45] cursor-pointer">
                <input
                  type="checkbox"
                  checked={Boolean(profile[key])}
                  onChange={(e) => setP(key, e.target.checked)}
                  className="accent-[#965EA7]"
                  data-testid={`signature-toggle-${key}`}
                />
                {label}
              </label>
            ))}
          </div>

          <SaveBar dirty={profileDirty} busy={busy === "profile"} onSave={saveProfile}
                   onReset={() => setProfile(savedProfile)} testid="signature-save-profile" />
        </Card>

        <Card title="Brand comun" icon={<Building2 size={16} />} testid="signature-card-brand">
          {!isOwner && (
            <div className="mb-3 rounded-xl bg-[#EDEEF0] border border-[#E5E7EB] p-3 text-sm text-[#6B7280] flex items-start gap-2"
                 data-testid="signature-brand-readonly-banner">
              <span className="inline-flex rounded-full bg-[#6B7280]/15 text-[#373A45] px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest shrink-0">Read-only</span>
              <span>
                Blocul comun apare în semnătura <strong>întregii echipe</strong>, așa că
                doar un <strong>owner</strong> îl poate modifica.
              </span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Denumire organizație" value={brand.org_name} max={LIMITS.org_name}
                   onChange={(v) => setB("org_name", v)} disabled={!isOwner}
                   testid="signature-org-name" />
            <Field label="Adresă site" value={brand.site_url} max={LIMITS.site_url}
                   onChange={(v) => setB("site_url", v)} disabled={!isOwner}
                   placeholder="https://ai.fped.org" testid="signature-site-url" />
            <Field label="Etichetă site" value={brand.site_label} max={LIMITS.site_label}
                   onChange={(v) => setB("site_label", v)} disabled={!isOwner}
                   placeholder="ai.fped.org" hint="Textul afișat pentru link."
                   testid="signature-site-label" />
            <Field label="Adresă poștală" value={brand.address} max={LIMITS.address}
                   onChange={(v) => setB("address", v)} disabled={!isOwner}
                   testid="signature-address" />
          </div>

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
            <Field label="URL logo" value={brand.logo_url} max={LIMITS.logo_url}
                   onChange={(v) => setB("logo_url", v)} disabled={!isOwner}
                   placeholder="https://ai.fped.org/assets/logo-email.png"
                   hint="Trebuie să fie https. Gmail blochează imaginile încorporate și pe cele http."
                   testid="signature-logo-url" />
            <div>
              <FieldLabel>Lățime logo</FieldLabel>
              <div className="mt-1 flex items-center gap-2">
                <input
                  type="number" min="24" max="240" step="1"
                  value={brand.logo_width}
                  onChange={(e) => setB("logo_width", Number(e.target.value))}
                  disabled={!isOwner}
                  className={inputCls}
                  data-testid="signature-logo-width"
                />
                <span className="text-xs text-[#6B7280]">px</span>
              </div>
            </div>
          </div>

          <div className="mt-3">
            <FieldLabel>Notă de confidențialitate</FieldLabel>
            <textarea
              value={brand.disclaimer}
              onChange={(e) => setB("disclaimer", e.target.value)}
              maxLength={LIMITS.disclaimer}
              rows={2}
              disabled={!isOwner}
              className={`mt-1 ${inputCls}`}
              data-testid="signature-disclaimer"
            />
          </div>

          {/* Socials */}
          <div className="mt-4">
            <FieldLabel>Linkuri sociale</FieldLabel>
            <div className="mt-1 space-y-2" data-testid="signature-socials">
              {socials.map((s, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    type="text" value={s.label || ""} placeholder="LinkedIn"
                    maxLength={LIMITS.social_label} disabled={!isOwner}
                    onChange={(e) => setB("socials", socials.map((x, j) => j === i ? { ...x, label: e.target.value } : x))}
                    className={`${inputCls} sm:max-w-[140px]`}
                    data-testid={`signature-social-label-${i}`}
                  />
                  <input
                    type="text" value={s.url || ""} placeholder="https://linkedin.com/company/fped"
                    maxLength={LIMITS.social_url} disabled={!isOwner}
                    onChange={(e) => setB("socials", socials.map((x, j) => j === i ? { ...x, url: e.target.value } : x))}
                    className={inputCls}
                    data-testid={`signature-social-url-${i}`}
                  />
                  {isOwner && (
                    <button
                      onClick={() => setB("socials", socials.filter((_, j) => j !== i))}
                      className="shrink-0 rounded-xl border border-[#E5E7EB] px-2.5 text-[#9b1f2c] hover:bg-[#9b1f2c]/5"
                      aria-label="Șterge linkul"
                      data-testid={`signature-social-remove-${i}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
              {isOwner && socials.length < MAX_SOCIALS && (
                <button
                  onClick={() => setB("socials", [...socials, { label: "", url: "" }])}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#965EA7] hover:underline"
                  data-testid="signature-social-add"
                >
                  <Plus size={14} /> Adaugă link
                </button>
              )}
              {!socials.length && !isOwner && (
                <div className="text-xs text-[#6B7280]">Niciun link configurat.</div>
              )}
            </div>
          </div>

          {/* Colors */}
          <div className="mt-4">
            <FieldLabel><Palette size={12} className="inline mr-1" />Culori</FieldLabel>
            <div className="mt-1 grid grid-cols-2 sm:grid-cols-4 gap-2">
              {COLORS.map(([key, label]) => (
                <div key={key}>
                  <div className="text-[11px] text-[#6B7280] mb-1">{label}</div>
                  <div className="flex items-center gap-1.5">
                    <input
                      type="color" value={brand[key] || "#000000"} disabled={!isOwner}
                      onChange={(e) => setB(key, e.target.value.toUpperCase())}
                      className="h-8 w-8 rounded-lg border border-[#E5E7EB] bg-white disabled:opacity-60"
                      data-testid={`signature-color-${key}`}
                    />
                    <input
                      type="text" value={brand[key] || ""} maxLength={7} disabled={!isOwner}
                      onChange={(e) => setB(key, e.target.value)}
                      className={`${inputCls} font-mono text-xs`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {isOwner && (
            <SaveBar dirty={brandDirty} busy={busy === "brand"} onSave={saveBrand}
                     onReset={() => setBrand(savedBrand)} testid="signature-save-brand"
                     note="Se aplică semnăturii întregii echipe." />
          )}
        </Card>
      </div>

      {/* ------------------------------ PREVIEW ---------------------------- */}
      <div className="xl:sticky xl:top-4">
        <Card title="Previzualizare" icon={<Eye size={16} />} testid="signature-card-preview">
          <p className="text-sm text-[#6B7280] mb-3">
            Exact HTML-ul care va fi copiat. Randat de backend, deci previzualizarea
            nu poate să difere de rezultat.
          </p>
          <SignaturePreview html={html} loading={previewing} />
          <ExportBlock html={html} text={text} dirty={profileDirty} />
          <div className="mt-3 text-xs text-[#6B7280] flex items-start gap-1.5">
            <Info size={13} className="mt-0.5 shrink-0" />
            <span>
              Fonturile de brand (Cabinet Grotesk, Outfit) nu pot fi folosite în email —
              niciun client nu le încarcă. Semnătura folosește Arial.
            </span>
          </div>
        </Card>
      </div>
    </div>
  );
}

/**
 * The preview renders inside an iframe on purpose.
 *
 * 1. Truthfulness — the dashboard's Tailwind reset would otherwise bleed into
 *    the signature and show something no email client will ever render.
 * 2. Isolation — `sandbox` without `allow-scripts` means nothing in the markup
 *    can execute. `allow-same-origin` is kept only so the parent can measure
 *    the document height for auto-sizing.
 */
function SignaturePreview({ html, loading }) {
  const ref = useRef(null);
  const [height, setHeight] = useState(180);

  const resize = useCallback(() => {
    try {
      const body = ref.current?.contentDocument?.body;
      if (body) setHeight(Math.max(80, body.scrollHeight + 32));
    } catch { /* cross-origin guard — keep the last known height */ }
  }, []);
  useEffect(() => { const t = setTimeout(resize, 50); return () => clearTimeout(t); }, [html, resize]);

  const srcDoc = `<!doctype html><meta charset="utf-8">
<body style="margin:0;padding:16px;background:#fff;font-family:Arial,Helvetica,sans-serif">${html || ""}</body>`;

  return (
    <div className={`relative rounded-xl border border-[#E5E7EB] overflow-hidden bg-white transition-opacity ${loading ? "opacity-60" : ""}`}>
      <iframe
        ref={ref}
        title="Previzualizare semnătură"
        srcDoc={srcDoc}
        sandbox="allow-same-origin"
        onLoad={resize}
        style={{ height }}
        className="w-full block border-0"
        data-testid="signature-preview-frame"
      />
      {loading && (
        <div className="absolute top-2 right-2 text-[#965EA7]">
          <Loader2 className="animate-spin" size={14} />
        </div>
      )}
    </div>
  );
}


/**
 * Copy + raw markup + the Gmail walkthrough.
 *
 * The copy button is the primary way this feature gets used, so it says what
 * happened rather than assuming success: the clipboard can be refused by the
 * browser (permissions, insecure context) and silently doing nothing would be
 * the worst outcome. The raw-HTML box is the escape hatch when it is.
 */
function ExportBlock({ html, text, dirty }) {
  const [copied, setCopied] = useState(null);
  const [showCode, setShowCode] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const flash = (which) => {
    setCopied(which);
    setTimeout(() => setCopied((c) => (c === which ? null : c)), 2000);
  };

  const doCopy = async () => {
    const via = await copyRichText(html, text);
    if (!via) {
      toast.error("Browserul nu a permis copierea. Folosește „Vezi codul HTML” de mai jos.");
      setShowCode(true);
      return;
    }
    flash("rich");
    toast.success("Semnătura a fost copiată. Lipește-o în Gmail.");
  };

  const doCopyCode = async () => {
    const via = await copyPlainText(html);
    if (!via) { toast.error("Nu am putut copia codul. Selectează-l manual."); return; }
    flash("code");
    toast.success("Codul HTML a fost copiat.");
  };

  return (
    <div className="mt-4" data-testid="signature-export">
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={doCopy}
          disabled={!html}
          className="inline-flex items-center gap-1.5 rounded-xl bg-[#965EA7] text-white px-4 py-2 text-sm font-semibold hover:bg-[#7A4986] disabled:opacity-60"
          data-testid="signature-copy"
        >
          {copied === "rich" ? <Check size={14} /> : <Copy size={14} />}
          {copied === "rich" ? "Copiat" : "Copiază semnătura"}
        </button>
        <button
          onClick={() => setShowHelp((v) => !v)}
          className="inline-flex items-center gap-1.5 rounded-xl border border-[#E5E7EB] px-3 py-2 text-sm text-[#373A45] hover:bg-[#F5F6F6]"
          data-testid="signature-help-toggle"
        >
          <Mail size={14} /> Cum o adaug în Gmail
          <ChevronDown size={13} className={showHelp ? "rotate-180 transition-transform" : "transition-transform"} />
        </button>
      </div>

      {dirty && (
        <div className="mt-2 text-xs text-[#8a6a1a] bg-[#FDF3E7] rounded-lg px-3 py-2" data-testid="signature-dirty-hint">
          Ai modificări nesalvate. Copierea ia ce vezi acum în previzualizare —
          salvează dacă vrei să le regăsești data viitoare.
        </div>
      )}

      {showHelp && (
        <ol className="mt-3 space-y-1.5 text-sm text-[#373A45] list-decimal pl-5" data-testid="signature-help">
          <li>Apasă <strong>Copiază semnătura</strong>.</li>
          <li>În Gmail: rotița ⚙ din dreapta sus → <strong>Afișează toate setările</strong>.</li>
          <li>Tab-ul <strong>General</strong>, derulează până la <strong>Semnătură</strong>.</li>
          <li><strong>Creează</strong> o semnătură nouă (sau alege una existentă).</li>
          <li>Dă <strong>lipire</strong> în casetă — <span className="text-[#6B7280]">Ctrl+V, sau ⌘V pe Mac</span>.</li>
          <li>Alege-o la <strong>„Pentru mesaje noi"</strong> și <strong>„La răspuns/redirecționare"</strong>.</li>
          <li>Jos de tot: <strong>Salvează modificările</strong>.</li>
        </ol>
      )}

      <button
        onClick={() => setShowCode((v) => !v)}
        className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#6B7280] hover:text-[#373A45]"
        data-testid="signature-code-toggle"
      >
        <Code2 size={13} /> {showCode ? "Ascunde codul HTML" : "Vezi codul HTML"}
      </button>

      {showCode && (
        <div className="mt-2">
          <textarea
            readOnly
            value={html}
            rows={6}
            onFocus={(e) => e.target.select()}
            className="w-full bg-[#F5F6F6] border border-[#E5E7EB] rounded-xl px-3 py-2 text-[11px] font-mono text-[#373A45] outline-none"
            data-testid="signature-code"
          />
          <div className="mt-1.5 flex items-center gap-2">
            <button
              onClick={doCopyCode}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#E5E7EB] px-2.5 py-1.5 text-xs font-semibold text-[#373A45] hover:bg-[#F5F6F6]"
              data-testid="signature-copy-code"
            >
              {copied === "code" ? <Check size={12} /> : <Copy size={12} />}
              {copied === "code" ? "Copiat" : "Copiază codul"}
            </button>
            <span className="text-[11px] text-[#6B7280]">
              {html.length.toLocaleString("ro-RO")} caractere · Gmail acceptă ~10.000
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls =
  "w-full bg-[#F5F6F6] border border-[#E5E7EB] rounded-xl px-3 py-2 text-sm text-[#373A45] focus:ring-2 focus:ring-[#965EA7] outline-none disabled:opacity-60";

function FieldLabel({ children }) {
  return (
    <label className="text-xs font-semibold uppercase tracking-widest text-[#373A45]">
      {children}
    </label>
  );
}

function Field({ label, value, onChange, max, placeholder, hint, disabled, testid }) {
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <input
        type="text"
        value={value || ""}
        maxLength={max}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-1 ${inputCls}`}
        data-testid={testid}
      />
      {hint && <div className="mt-1 text-[11px] text-[#6B7280]">{hint}</div>}
    </div>
  );
}

function SaveBar({ dirty, busy, onSave, onReset, testid, note }) {
  if (!dirty) {
    return note ? <div className="mt-4 text-xs text-[#6B7280]">{note}</div> : null;
  }
  return (
    <div className="mt-4 flex items-center gap-2">
      <button
        onClick={onSave}
        disabled={busy}
        className="inline-flex items-center gap-1.5 rounded-xl bg-[#965EA7] text-white px-4 py-2 text-sm font-semibold hover:bg-[#7A4986] disabled:opacity-60"
        data-testid={testid}
      >
        {busy ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
        Salvează
      </button>
      <button
        onClick={onReset}
        disabled={busy}
        className="rounded-xl border border-[#E5E7EB] px-3 py-2 text-sm text-[#6B7280] hover:bg-[#F5F6F6] disabled:opacity-60"
      >
        Renunță
      </button>
      {note && <span className="text-xs text-[#6B7280]">{note}</span>}
    </div>
  );
}

function Card({ title, icon, children, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-[#965EA7] mb-3">
        {icon}{title}
      </div>
      {children}
    </div>
  );
}
